
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { useSettingsStore } from "@/store/api/settingsStore";
import { toast } from "sonner";

export function RolesTab() {
  const { t } = useTranslation("settings");
  const tCommon = (key: string) => t(key, { ns: "common" });
  const { settings, updateSettings, isLoading, fetchSettings } = useSettingsStore();
  
  const [defaultRole, setDefaultRole] = useState(settings.roles.defaultRole);
  const [permissions, setPermissions] = useState(settings.roles.permissions);

  // Fetch settings when component mounts
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update local state when settings change
  useEffect(() => {
    setDefaultRole(settings.roles.defaultRole);
    setPermissions(settings.roles.permissions);
  }, [settings]);

  const handleRoleChange = (value: string) => {
    setDefaultRole(value);
  };

  const handlePermissionChange = (key: keyof typeof permissions, value: boolean) => {
    setPermissions(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      await updateSettings({
        roles: {
          defaultRole,
          permissions
        }
      });
      toast.success(tCommon('saveSuccess'));
    } catch (error) {
      toast.error(tCommon('saveFailed'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('roles.title')}</CardTitle>
        <CardDescription>{t('roles.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <Label>{t('roles.defaultRole')}</Label>
          <RadioGroup value={defaultRole} onValueChange={handleRoleChange}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="admin" id="admin" />
              <Label htmlFor="admin">{t('roles.adminRole')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="trainer" id="trainer" />
              <Label htmlFor="trainer">{t('roles.trainerRole')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="user" id="user" />
              <Label htmlFor="user">{t('roles.userRole')}</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="space-y-4">
          <Label>{t('roles.permissions')}</Label>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="trainer-permissions" className="flex-1">{t('roles.trainerCanEditClasses')}</Label>
              <Switch 
                id="trainer-permissions" 
                checked={permissions.trainerCanEditClasses} 
                onCheckedChange={(value) => handlePermissionChange('trainerCanEditClasses', value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="user-view-all" className="flex-1">{t('roles.userCanViewAllClients')}</Label>
              <Switch 
                id="user-view-all" 
                checked={permissions.userCanViewAllClients} 
                onCheckedChange={(value) => handlePermissionChange('userCanViewAllClients', value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="admin-only-payments" className="flex-1">{t('roles.adminOnlyPayments')}</Label>
              <Switch 
                id="admin-only-payments" 
                checked={permissions.adminOnlyPayments} 
                onCheckedChange={(value) => handlePermissionChange('adminOnlyPayments', value)}
              />
            </div>
          </div>
        </div>
        
        <Button onClick={handleSave} disabled={isLoading}>
          {isLoading ? tCommon('saving') : tCommon('save')}
        </Button>
      </CardContent>
    </Card>
  );
}
